import { create } from "zustand";

/**
 * Act-As (admin impersonation) store.
 *
 * Persists to localStorage via manual reads/writes — mirrors the pattern in
 * `theme-store.ts`. Expired entries are auto-cleared on read.
 *
 * The `X-Act-As-User` header injection and 403 auto-clear live in
 * `lib/api-client.ts`, which subscribes to this store.
 */

interface ActAsState {
  actingAsUserId: string | null;
  actingAsEmail: string | null;
  /** Unix ms. */
  expiresAt: number | null;
  setActingAs: (userId: string, email: string, expiresAt: number) => void;
  clearActingAs: () => void;
}

const STORAGE_KEY = "paperchase-act-as";

interface StoredShape {
  actingAsUserId: string | null;
  actingAsEmail: string | null;
  expiresAt: number | null;
}

function readStored(): Partial<StoredShape> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<StoredShape>;
    // Auto-expire on read.
    if (
      typeof parsed.expiresAt === "number" &&
      parsed.expiresAt < Date.now()
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

function writeStored(data: StoredShape) {
  if (typeof localStorage === "undefined") return;
  if (data.actingAsUserId === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}

const initial = readStored();

export const useActAsStore = create<ActAsState>((set) => ({
  actingAsUserId: initial.actingAsUserId ?? null,
  actingAsEmail: initial.actingAsEmail ?? null,
  expiresAt: initial.expiresAt ?? null,
  setActingAs: (userId, email, expiresAt) => {
    writeStored({
      actingAsUserId: userId,
      actingAsEmail: email,
      expiresAt,
    });
    set({
      actingAsUserId: userId,
      actingAsEmail: email,
      expiresAt,
    });
  },
  clearActingAs: () => {
    writeStored({
      actingAsUserId: null,
      actingAsEmail: null,
      expiresAt: null,
    });
    set({
      actingAsUserId: null,
      actingAsEmail: null,
      expiresAt: null,
    });
  },
}));

/**
 * Pure predicate — true iff there's an active act-as that hasn't expired.
 * Accepts any slice with `actingAsUserId` and `expiresAt` so callers can pass
 * either the whole store state or a snapshot.
 */
export function isActAsActive(
  state: Pick<ActAsState, "actingAsUserId" | "expiresAt">,
): boolean {
  return (
    !!state.actingAsUserId &&
    !!state.expiresAt &&
    state.expiresAt > Date.now()
  );
}
